/**
 * Subscription Types for What 2 Do ?
 * Defines plans, subscription status, and payment-related types
 */

// ============================================
// Plan Types
// ============================================

export type PlanType = 'free' | 'monthly' | 'yearly' | 'pay_per_event';

// Alias for compatibility with existing code
export type SubscriptionPlanId = PlanType;

export interface SubscriptionPlan {
  id: PlanType;
  name: string;
  nameShort: string;
  price: number;           // in cents (2000 = $20)
  regularPrice?: number;   // Future regular price in cents (for launch pricing display)
  interval: 'month' | 'year' | 'one_time' | null;
  eventLimit: number | null; // null = unlimited
  features: string[];
  recommended?: boolean;
}

// ============================================
// Subscription Status
// ============================================

// Note: "canceled" (US spelling) matches Stripe API, "cancelled" (UK) for display
export type SubscriptionStatus = 'active' | 'canceled' | 'cancelled' | 'past_due' | 'trialing' | 'expired' | 'incomplete';

export interface OrganizerSubscription {
  organizerId: string;
  planId: PlanType;
  status: SubscriptionStatus;
  currentPeriodStart?: Date;
  currentPeriodEnd: Date;
  eventsUsed: number;        // For free and pay_per_event plans
  eventsLimit: number | null; // null = unlimited
  cancelAtPeriodEnd?: boolean;

  // Payment provider IDs (optional, for future Stripe/PayPal integration)
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  paypalSubscriptionId?: string;

  createdAt?: Date;
  updatedAt?: Date;
}

// ============================================
// Payment History (for future use)
// ============================================

export type PaymentStatus = 'succeeded' | 'failed' | 'pending' | 'refunded';
export type PaymentProvider = 'stripe' | 'paypal';

export interface PaymentHistory {
  id: string;
  organizerId: string;
  amount: number;            // in cents
  currency: string;          // 'USD'
  status: PaymentStatus;
  provider: PaymentProvider;
  description: string;
  invoiceUrl?: string;
  createdAt: Date;
}

// ============================================
// Plan Constants
// ============================================

export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    id: 'free',
    name: 'Free',
    nameShort: 'Free',
    price: 0,
    interval: null,
    eventLimit: 5,
    features: [
      '5 events maximum',
      'Full analytics',
      'Public profile'
    ]
  },
  {
    id: 'monthly',
    name: 'Pro Monthly',
    nameShort: 'Pro',
    price: 2000, // $20
    regularPrice: 3000, // $30 (future regular price)
    interval: 'month',
    eventLimit: null,
    features: [
      'Unlimited events',
      'Refer & earn free months'
    ],
    recommended: true
  },
  {
    id: 'yearly',
    name: 'Pro Yearly',
    nameShort: 'Pro',
    price: 16000, // $160/year (4 months free)
    regularPrice: 24000, // $240/year (future regular price)
    interval: 'year',
    eventLimit: null,
    features: [
      'Unlimited events',
      '4 months free',
      'Refer & earn free months'
    ]
  },
  {
    id: 'pay_per_event',
    name: 'Pay per Event',
    nameShort: 'Pay/Event',
    price: 400, // $4 per event
    interval: 'one_time',
    eventLimit: 1, // Per purchase
    features: [
      '1 additional event',
      'No commitment',
      'Pay as you go'
    ]
  }
];

// ============================================
// Helper Functions
// ============================================

/**
 * Get a subscription plan by ID
 */
export const getPlanById = (planId: PlanType): SubscriptionPlan => {
  return SUBSCRIPTION_PLANS.find(plan => plan.id === planId) || SUBSCRIPTION_PLANS[0];
};

/**
 * Format price for display (cents to USD)
 */
export const formatPrice = (priceInCents: number): string => {
  return `${(priceInCents / 100).toFixed(0)}`;
};

/**
 * Get price with interval label
 */
export const formatPriceWithInterval = (plan: SubscriptionPlan): string => {
  if (plan.price === 0) return 'Free';

  const price = formatPrice(plan.price);

  switch (plan.interval) {
    case 'month':
      return `$${price}/month`;
    case 'year':
      return `$${price}/year`;
    case 'one_time':
      return `$${price}/event`;
    default:
      return `$${price}`;
  }
};

/**
 * Check if a plan is a Pro plan (unlimited events)
 */
export const isPro = (planId: PlanType): boolean => {
  return planId === 'monthly' || planId === 'yearly';
};

/**
 * Get launch pricing info for a plan
 * Returns whether it's a launch price and the formatted regular price
 */
export const getLaunchPriceInfo = (plan: SubscriptionPlan): {
  isLaunchPrice: boolean;
  regularPriceFormatted: string | null;
} => {
  if (!plan.regularPrice || plan.regularPrice <= plan.price) {
    return { isLaunchPrice: false, regularPriceFormatted: null };
  }
  return {
    isLaunchPrice: true,
    regularPriceFormatted: `$${formatPrice(plan.regularPrice)}`,
  };
};

/**
 * Get default subscription for new organizers
 */
export const getDefaultSubscription = (organizerId: string): OrganizerSubscription => {
  const now = new Date();
  return {
    organizerId,
    planId: 'free',
    status: 'active',
    currentPeriodStart: now,
    currentPeriodEnd: new Date(now.getFullYear() + 100, now.getMonth(), now.getDate()), // Far future for free
    eventsUsed: 0,
    eventsLimit: 5,
    createdAt: now,
    updatedAt: now
  };
};
